import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL, getErrorMessage } from '../constants';

export default function useSignatureDrafts({
  identite, operation, technique, headers, config, result,
  fetchSimulations, setSavedSimulation, setActiveTab, restoreDraftData,
}) {
  const [signingInProgress, setSigningInProgress] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [signatureReference, setSignatureReference] = useState(null);
  const [signatureMethod, setSignatureMethod] = useState(null);
  const [signatureDestination, setSignatureDestination] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);

  const handleInitiateSignature = async (reference, method, customDestination) => {
    setSigningInProgress(true);
    try {
      const signerPhone = method === 'sms' ? (customDestination || identite.telephone) : identite.telephone;
      const signerEmail = method === 'email' ? (customDestination || identite.email) : identite.email;
      const res = await axios.post(`${BACKEND_URL}/api/domium/signature/initiate`, {
        reference, signer_name: identite.nom || identite.raison_sociale,
        signer_email: signerEmail, signer_phone: signerPhone, method,
      }, { headers });
      if (res.data.success) {
        setOtpSent(true);
        setSignatureReference(res.data.signature_reference);
        setSignatureMethod(method);
        setSignatureDestination(res.data.otp_sent_to || (method === 'sms' ? signerPhone : signerEmail));
        toast.success(method === 'sms' ? 'Code envoye par SMS' : 'Code envoye par email');
      }
    } catch (error) { toast.error(getErrorMessage(error, "Erreur lors de l'envoi du code")); }
    finally { setSigningInProgress(false); }
  };

  const handleValidateSignature = async (reference) => {
    if (!otpCode || otpCode.length !== 6) { toast.error('Veuillez entrer le code a 6 chiffres'); return; }
    setSigningInProgress(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/domium/signature/validate`,
        { reference, otp_code: otpCode }, { headers });
      if (res.data.success) {
        toast.success('Signature validee ! Les documents sont prets.');
        setOtpSent(false); setOtpCode('');
        fetchSimulations();
        const updated = await axios.get(`${BACKEND_URL}/api/domium/subscribe/${reference}`, { headers });
        if (updated.data?.success && updated.data?.simulation) {
          setSavedSimulation(prev => ({
            ...prev, ...updated.data.simulation,
            signature: updated.data.simulation?.signature || prev?.signature,
          }));
        }
      }
    } catch (error) { toast.error(getErrorMessage(error, 'Code invalide')); }
    finally { setSigningInProgress(false); }
  };

  const handleResendOTP = async (reference) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/domium/signature/resend`, { reference }, { headers });
      if (res.data.success) toast.success('Nouveau code envoye');
    } catch { toast.error('Erreur lors du renvoi du code'); }
  };

  const handleSaveDraft = async (memberId = 'default_member') => {
    setSavingDraft(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/domium/subscribe/draft/save`, {
        member_id: memberId, identite, operation, technique,
        calcul: result?.calcul, affectation: result?.affectation,
        documents_requis: config?.documents_requis?.[identite.qualite] || [],
      }, { headers });
      if (res.data.success) {
        toast.success(`Brouillon sauvegarde jusqu'au ${new Date(res.data.expires_at).toLocaleDateString('fr-FR')}`);
        fetchSimulations();
      }
    } catch (error) { toast.error(getErrorMessage(error, 'Erreur sauvegarde brouillon')); }
    finally { setSavingDraft(false); }
  };

  const handleResumeDraft = async (reference) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/domium/subscribe/draft/${reference}/resume`, { headers });
      if (res.data?.success && res.data?.draft) {
        restoreDraftData(res.data.draft);
        setSavedSimulation(res.data.draft);
        toast.success(`Brouillon charge - ${res.data.days_remaining || 30} jours restants`);
        setActiveTab('identite');
      } else { toast.error('Brouillon invalide'); }
    } catch (error) { toast.error(getErrorMessage(error, 'Brouillon non trouve ou expire')); }
  };

  const handleFinalizeDraft = async (reference) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/domium/subscribe/draft/${reference}/finalize`, {}, { headers });
      if (res.data?.success) {
        toast.success('Souscription finalisee - Prete pour signature');
        fetchSimulations();
        const updated = await axios.get(`${BACKEND_URL}/api/domium/subscribe/${reference}`, { headers });
        if (updated.data?.success && updated.data?.simulation) {
          setSavedSimulation(updated.data.simulation);
          setActiveTab('resultat');
        }
      }
    } catch (error) { toast.error(getErrorMessage(error, 'Donnees incompletes')); }
  };

  return {
    signingInProgress, otpSent, otpCode, setOtpCode,
    signatureReference, signatureMethod, signatureDestination,
    savingDraft,
    handleInitiateSignature, handleValidateSignature, handleResendOTP,
    handleSaveDraft, handleResumeDraft, handleFinalizeDraft,
  };
}
